import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Breadcrumbs, Button, Card, CardContent, Link, Skeleton, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import PageBand from "../../layouts/page-detail/PageBand";
import WorkflowRunRail from "./WorkflowRunRail";
import { WorkflowRunSource } from "../../hooks/useWorkflowRunSource";
import { isPifDefinitionToken } from "./pifWorkflowKeys";

const BREADCRUMB_LINK_COLOR = "rgba(255, 255, 255, 0.75)";

interface WorkflowRunPageProps {
  /** Shown as the last breadcrumb once there is a case to name. */
  reference?: string;
  children: React.ReactNode;
}

/** The Test / Run page chrome — date-picker provider, page band and breadcrumbs. */
export const WorkflowRunPage: React.FC<WorkflowRunPageProps> = ({ reference, children }) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <main className="workflow-run">
      <PageBand
        title="Test / Run"
        breadcrumbs={
          <Breadcrumbs
            aria-label="breadcrumbs"
            sx={{ "& .MuiBreadcrumbs-separator": { color: BREADCRUMB_LINK_COLOR } }}
          >
            <Link component={RouterLink} to="/workflow/library" underline="hover" sx={{ color: BREADCRUMB_LINK_COLOR }}>
              Workflow Management
            </Link>
            <Typography sx={{ fontWeight: 600, color: "#fff" }}>Test / Run</Typography>
            {reference && <Typography sx={{ fontWeight: 600, color: "#fff" }}>{reference}</Typography>}
          </Breadcrumbs>
        }
      />
      {children}
    </main>
  </LocalizationProvider>
);

const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <WorkflowRunPage>
    <section className="workflow-run__layout workflow-run__layout--empty">
      <Typography className="workflow-run__empty-stage">{children}</Typography>
    </section>
  </WorkflowRunPage>
);

const LoadError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <WorkflowRunPage>
    <section className="workflow-run__layout workflow-run__layout--empty">
      <Alert
        className="workflow-run__alert"
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {message}
      </Alert>
    </section>
  </WorkflowRunPage>
);

const Loading: React.FC = () => (
  <WorkflowRunPage>
    <section className="workflow-run__layout" aria-label="Workflow runtime" aria-busy="true">
      <Box className="workflow-run__main">
        <Card className="workflow-run__stage" variant="outlined">
          <CardContent className="workflow-run__stage-body">
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="rounded" height={44} sx={{ mt: 2 }} />
            <Skeleton variant="rounded" height={44} sx={{ mt: 2 }} />
            <Skeleton variant="rounded" height={44} sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      </Box>
      <WorkflowRunRail loading />
    </section>
  </WorkflowRunPage>
);

const LibraryLink: React.FC<{ label?: string }> = ({ label = "Library" }) => (
  <Link component={RouterLink} to="/workflow/library" underline="hover">
    {label}
  </Link>
);

interface WorkflowRunSourceScreenProps {
  source: WorkflowRunSource;
  /** The real case whose stage and values are already in the screen's state, if any. */
  appliedInstanceId: number | null;
  /** Whether the viewer can edit in the Builder - only they can publish a draft. */
  canEditBuilder: boolean;
}

/**
 * Everything the Run screen shows instead of a stage (a plain render function, not a component - the
 * page needs to know whether it returned anything): a real definition or case still loading,
 * refused (404 / 403), or failed with Retry. Renders nothing once there is a stage to show — a PIF
 * waiting on its HR gate included, since pif-1 renders with no case behind it.
 */
export const renderWorkflowRunSourceScreen = ({
  source,
  appliedInstanceId,
  canEditBuilder,
}: WorkflowRunSourceScreenProps): React.ReactElement | null => {
  const { realStart, definitionPreview, caseStart, realInstanceId, caseLoad, runCase } = source;

  // A share link (/wf/{RefNo}) until it is resolved to its case. Same refusals as opening the case.
  if (source.refNo) {
    const { refNoLookup } = source;
    if (refNoLookup.status === "not-found") {
      return (
        <EmptyState>
          No case matches {source.refNo}. Open one from the <LibraryLink /> or{" "}
          <Link component={RouterLink} to="/workflow/instances" underline="hover">
            Instances
          </Link>
          .
        </EmptyState>
      );
    }
    if (refNoLookup.status === "forbidden") {
      return <EmptyState>You don&apos;t have access to this case.</EmptyState>;
    }
    if (refNoLookup.status === "error") {
      return <LoadError message="This case couldn't be loaded." onRetry={source.retryRefNoLookup} />;
    }
    return <Loading />;
  }

  if (realStart && definitionPreview.status !== "loaded") {
    if (definitionPreview.status === "not-found" && "token" in realStart) {
      // By Token, only a published version is ever returned — drafts and archived ones are not.
      // The PIF is started by supervisors, not Builder users, so it gets the agreed plain message.
      if (isPifDefinitionToken(realStart.token)) {
        return <EmptyState>The New Hire PIF isn&apos;t available right now. Please contact HR.</EmptyState>;
      }
      return <EmptyState>This workflow has no published version to run yet.</EmptyState>;
    }
    if (definitionPreview.status === "not-found") {
      return (
        <EmptyState>
          This workflow can&apos;t be found — it may have been deleted. Pick one from the <LibraryLink />.
        </EmptyState>
      );
    }
    if (definitionPreview.status === "forbidden") {
      return <EmptyState>You don&apos;t have access to this workflow.</EmptyState>;
    }
    if (definitionPreview.status === "error") {
      return <LoadError message="This workflow couldn't be loaded." onRetry={source.retryDefinitionPreview} />;
    }
    return <Loading />;
  }

  if (realStart && !source.isPifPreStart) {
    if (caseStart.status === "not-found") {
      return (
        <EmptyState>
          This workflow can&apos;t be started — it may be unpublished or archived. Pick one from the{" "}
          <LibraryLink />.
        </EmptyState>
      );
    }
    if (caseStart.status === "not-runnable") {
      // Retry could never succeed, so none is offered. A Builder test run may start a draft (Epic
      // 80431), so from the Builder this means the graph isn't runnable yet (no single start stage);
      // anywhere else, only a published version runs - and only a Builder editor can publish it.
      let notRunnableMessage =
        "This workflow isn't published yet, so it can't run. Ask a workflow administrator to publish it.";
      if (source.fromBuilder) {
        notRunnableMessage =
          "This draft can't run yet. Validate it in the Builder to see what's missing, then use Preview / Run.";
      } else if (canEditBuilder) {
        notRunnableMessage = "This workflow isn't published yet, so it can't run. Publish it in the Builder first.";
      }
      return <EmptyState>{notRunnableMessage}</EmptyState>;
    }
    if (caseStart.status === "forbidden") {
      return <EmptyState>You don&apos;t have permission to start this workflow.</EmptyState>;
    }
    if (caseStart.status === "error") {
      return <LoadError message="This workflow couldn't be started." onRetry={source.retryCaseStart} />;
    }
    // Waiting for the idempotency key, the start call, or the redirect to the new case.
    return <Loading />;
  }

  // Until the loaded case is in the screen's state the skeleton stays up, so a resumed case never
  // flashes stage 1 with empty fields first; a reload of the same case keeps the current render.
  if (realInstanceId && (caseLoad.status !== "loaded" || appliedInstanceId !== runCase?.instanceId)) {
    if (caseLoad.status === "not-found") {
      return (
        <EmptyState>
          No instance selected. Open one from the <LibraryLink /> or{" "}
          <Link component={RouterLink} to="/workflow/instances" underline="hover">
            Instances
          </Link>
          .
        </EmptyState>
      );
    }
    if (caseLoad.status === "forbidden") {
      // Nothing about the case is rendered — not even its reference number.
      return <EmptyState>You don&apos;t have access to this case.</EmptyState>;
    }
    if (caseLoad.status === "error") {
      return <LoadError message="This case couldn't be loaded." onRetry={source.retryCaseLoad} />;
    }
    return <Loading />;
  }

  if (!source.definition || source.definition.canvases.length === 0) {
    return (
      <EmptyState>
        No workflow selected. Open one from the <LibraryLink label="Workflow Library" /> to run it.
      </EmptyState>
    );
  }

  return null;
};
